import type { TranslationKey } from '../../core/i18n/translation';

/**
 * One section of the legal page: a small label, a heading, an opening paragraph, an optional list,
 * an optional highlighted box and an optional closing paragraph.
 *
 * **The shape is the page's, not a guess.** Every section of the live page is built from those
 * parts, so describing them once and rendering the list is what keeps six hundred lines of markup
 * from being copied across — and what makes a missing translation a compile error rather than a
 * blank block, since every field here is a `TranslationKey`.
 *
 * **The `label` doubles as the contents-rail entry**, numbered by position when it is rendered
 * there. The live page keeps a second, hand-numbered list of Kazakh and Russian labels in its own
 * script, four of which say something slightly different from the badge on the section they point
 * at — thirty-two strings whose only job is to disagree with thirty-two others.
 */
export interface PolicySection {
    readonly id: string;
    readonly label: TranslationKey;
    readonly heading: TranslationKey;
    readonly intro: TranslationKey;
    /** An optional pair of sub-headed lists, which only the "what we collect" section has. */
    readonly groups?: readonly {
        readonly heading: TranslationKey;
        readonly items: readonly TranslationKey[];
    }[];
    readonly items?: readonly TranslationKey[];
    readonly box?: TranslationKey;
    /**
     * How the box reads. `info` is the brand-coloured note, `warn` the amber one — the live page
     * uses each twice, and which of the two a section gets is part of what it says.
     */
    readonly boxTone?: 'info' | 'warn';
    readonly outro?: TranslationKey;
    /** A contact block: a heading over the company's own email address and telephone number. */
    readonly contact?: TranslationKey;
    /**
     * Whether the section ends with the cookie-preferences control.
     *
     * Only the cookies section does. The right to withdraw consent is stated in the prose two
     * sections above it, and this is where a reader looking for the control will look.
     */
    readonly cookiePreferences?: boolean;
}

export const privacySections: readonly PolicySection[] = [
    {
        id: 'intro',
        label: 'policy.pp.intro.label',
        heading: 'policy.pp.intro.h2',
        intro: 'policy.pp.intro.p1',
        box: 'policy.pp.intro.box',
        boxTone: 'info'
    },
    {
        id: 'collect',
        label: 'policy.pp.collect.label',
        heading: 'policy.pp.collect.h2',
        intro: 'policy.pp.collect.p1',
        groups: [
            {
                heading: 'policy.pp.collect.h3a',
                items: ['policy.pp.collect.a1', 'policy.pp.collect.a2', 'policy.pp.collect.a3']
            },
            {
                heading: 'policy.pp.collect.h3b',
                items: ['policy.pp.collect.b1', 'policy.pp.collect.b2', 'policy.pp.collect.b3', 'policy.pp.collect.b4']
            }
        ]
    },
    {
        id: 'use',
        label: 'policy.pp.use.label',
        heading: 'policy.pp.use.h2',
        intro: 'policy.pp.use.p1',
        items: ['policy.pp.use.l1', 'policy.pp.use.l2', 'policy.pp.use.l3', 'policy.pp.use.l4', 'policy.pp.use.l5'],
        box: 'policy.pp.use.box',
        boxTone: 'warn'
    },
    {
        id: 'share',
        label: 'policy.pp.share.label',
        heading: 'policy.pp.share.h2',
        intro: 'policy.pp.share.p1',
        items: ['policy.pp.share.l1', 'policy.pp.share.l2', 'policy.pp.share.l3'],
        outro: 'policy.pp.share.p2'
    },
    {
        id: 'cookies',
        label: 'policy.pp.cookies.label',
        heading: 'policy.pp.cookies.h2',
        intro: 'policy.pp.cookies.p1',
        items: ['policy.pp.cookies.l1', 'policy.pp.cookies.l2', 'policy.pp.cookies.l3'],
        cookiePreferences: true
    },
    {
        id: 'rights',
        label: 'policy.pp.rights.label',
        heading: 'policy.pp.rights.h2',
        intro: 'policy.pp.rights.p1',
        items: [
            'policy.pp.rights.l1',
            'policy.pp.rights.l2',
            'policy.pp.rights.l3',
            'policy.pp.rights.l4',
            'policy.pp.rights.l5'
        ],
        outro: 'policy.pp.rights.p2'
    },
    {
        id: 'security',
        label: 'policy.pp.security.label',
        heading: 'policy.pp.security.h2',
        intro: 'policy.pp.security.p1',
        items: ['policy.pp.security.l1', 'policy.pp.security.l2', 'policy.pp.security.l3', 'policy.pp.security.l4']
    },
    {
        id: 'contact',
        label: 'policy.pp.contact.label',
        heading: 'policy.pp.contact.h2',
        intro: 'policy.pp.contact.p1',
        contact: 'policy.pp.contact.h4'
    }
];

export const termsSections: readonly PolicySection[] = [
    {
        id: 'intro',
        label: 'policy.tt.intro.label',
        heading: 'policy.tt.intro.h2',
        intro: 'policy.tt.intro.p1',
        box: 'policy.tt.intro.box',
        boxTone: 'info'
    },
    {
        id: 'elig',
        label: 'policy.tt.elig.label',
        heading: 'policy.tt.elig.h2',
        intro: 'policy.tt.elig.p1',
        items: ['policy.tt.elig.l1', 'policy.tt.elig.l2', 'policy.tt.elig.l3', 'policy.tt.elig.l4']
    },
    {
        id: 'ip',
        label: 'policy.tt.ip.label',
        heading: 'policy.tt.ip.h2',
        intro: 'policy.tt.ip.p1',
        items: ['policy.tt.ip.l1', 'policy.tt.ip.l2', 'policy.tt.ip.l3']
    },
    {
        id: 'prohib',
        label: 'policy.tt.prohib.label',
        heading: 'policy.tt.prohib.h2',
        intro: 'policy.tt.prohib.p1',
        items: [
            'policy.tt.prohib.l1',
            'policy.tt.prohib.l2',
            'policy.tt.prohib.l3',
            'policy.tt.prohib.l4',
            'policy.tt.prohib.l5'
        ],
        box: 'policy.tt.prohib.box',
        boxTone: 'warn'
    },
    {
        id: 'disc',
        label: 'policy.tt.disc.label',
        heading: 'policy.tt.disc.h2',
        intro: 'policy.tt.disc.p1',
        items: ['policy.tt.disc.l1', 'policy.tt.disc.l2', 'policy.tt.disc.l3', 'policy.tt.disc.l4'],
        outro: 'policy.tt.disc.p2'
    },
    {
        id: 'links',
        label: 'policy.tt.links.label',
        heading: 'policy.tt.links.h2',
        intro: 'policy.tt.links.p1'
    },
    {
        id: 'changes',
        label: 'policy.tt.changes.label',
        heading: 'policy.tt.changes.h2',
        intro: 'policy.tt.changes.p1',
        items: ['policy.tt.changes.l1', 'policy.tt.changes.l2', 'policy.tt.changes.l3'],
        outro: 'policy.tt.changes.p2'
    },
    {
        id: 'law',
        label: 'policy.tt.law.label',
        heading: 'policy.tt.law.h2',
        intro: 'policy.tt.law.p1',
        contact: 'policy.tt.law.h4'
    }
];
